import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem, MessageService } from "primeng/api";
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from "primeng/progressbar";
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from "primeng/toast";
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from "../../../services/session-storage.service";
import { AccountingReportsService, LedgerReportSummary, ReportDiagnostic } from "../../accounting-reports.service";
import { PartnerLedger } from "./partner-ledger.model";
import { PartnerLedgerSummary } from "./partner-ledger-summary.model";

interface PartnerOption {
    label: string;
    partnerId: number;
    partnerType: string;
}

interface DisplayPartnerLedgerSummary extends PartnerLedgerSummary {
    partnerKey: string;
    accountRoles: string[];
    accountLabels: string[];
    openBalance: number;
}

@Component({
    selector: 'app-partner-ledger',
    templateUrl: './partner-ledger.component.html',
    styleUrls: ['./partner-ledger.component.css'],
    providers: [
        MessageService,
        CurrencyPipe
    ],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        TableModule,
        ButtonModule,
        TooltipModule,
        DatePickerModule,
        ToastModule,
        ProgressBarModule,
        IftaLabelModule,
        MultiSelectModule,
        SelectModule,
        InputTextModule,
        TagModule,
        ChipModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class PartnerLedgerComponent implements OnInit {

    breadcrumbItems: MenuItem[] | undefined;
    homeBreadcrumb: MenuItem | undefined;

    fullPartnerLedgerSummaries: DisplayPartnerLedgerSummary[] = [];
    selectedPartnerLedgerSummaries: DisplayPartnerLedgerSummary[] = [];
    fullPartnerLedgers: PartnerLedger[] = [];

    isLoading: boolean = false;
    first = 0;
    rows = 10;
    diagnostics: ReportDiagnostic[] = [];
    metrics: PageMetric[] = [];

    filterDateRange: Date[] | null = null;
    filterPartnerTypes: string[] = [];
    selectedPartnerOptions: PartnerOption[] = [];
    filterJournals: string[] = [];
    filterCurrencyCodes: string[] = [];
    filterAccountRole: string | null = null;
    filterTransactionNumber: string | null = null;
    filterReference: string | null = null;
    filterDescription: string | null = null;
    filterLogisticsProjectId: number | null = null;

    partnerOptions: PartnerOption[] = [];

    partnerTypeOptions: string[] = [];
    journalOptions: string[] = [];
    currencyOptions: string[] = [];
    accountRoleOptions = [
        { label: 'Receivable', value: 'RECEIVABLE' },
        { label: 'Payable', value: 'PAYABLE' },
        { label: 'Both', value: 'BOTH' }
    ];

    private reportCurrencyCode = 'SAR';

    constructor(
        private accountingReportsService: AccountingReportsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private currencyPipe: CurrencyPipe
    ) { }

    get visiblePartnerOptions(): PartnerOption[] {
        if (!this.filterPartnerTypes.length) {
            return this.partnerOptions;
        }
        return this.partnerOptions.filter(option => this.filterPartnerTypes.includes(option.partnerType));
    }

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Accounts', routerLink: '/accounts' },
            { label: 'Enterprise', routerLink: '/accounts/enterprise' },
            { label: 'Partner Ledger' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        this.updateMetrics();

        this.loadPartnerLedgerSummary();
    }

    applyFilters(): void {
        this.first = 0;
        this.loadPartnerLedgerSummary();
    }

    clearFilters(): void {
        this.filterDateRange = null;
        this.filterPartnerTypes = [];
        this.selectedPartnerOptions = [];
        this.filterJournals = [];
        this.filterCurrencyCodes = [];
        this.filterAccountRole = null;
        this.filterTransactionNumber = null;
        this.filterReference = null;
        this.filterDescription = null;
        this.filterLogisticsProjectId = null;
        this.applyFilters();
    }

    onPartnerTypeChange(): void {
        if (!this.filterPartnerTypes.length) {
            return;
        }
        this.selectedPartnerOptions = this.selectedPartnerOptions.filter(option =>
            this.filterPartnerTypes.includes(option.partnerType)
        );
    }

    loadPartnerLedgerSummary(): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'Company ID was not found.' });
            return;
        }

        this.isLoading = true;
        this.accountingReportsService.getPartnerLedgerV2(companyId, 0, 100000, this.getCurrentFilters())
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (response) => {
                    this.diagnostics = response.diagnostics || [];
                    this.fullPartnerLedgers = this.normalizeLedgerRows(response.content);
                    this.mergeFilterOptions(this.fullPartnerLedgers);
                    this.fullPartnerLedgerSummaries = this.buildSummaries(this.fullPartnerLedgers);
                    this.selectedPartnerLedgerSummaries = [...this.fullPartnerLedgerSummaries];
                    this.reportCurrencyCode = response.summary?.currencyCode || this.reportCurrencyCode;
                    this.updateMetrics(response.summary);
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Error loading Partner Ledger.'
                    });
                }
            });
    }

    rowCurrency(row: PartnerLedger, summary?: DisplayPartnerLedgerSummary): string {
        return this.safeCurrencyCode(row.currencyCode || summary?.currencyCode || this.reportCurrencyCode);
    }

    sourceLabel(row: PartnerLedger): string {
        const type = row.sourceDocumentType || 'Journal Entry';
        const number = row.sourceDocumentNumber || row.transactionNumber || row.sourceDocumentId;
        return number ? `${type}: ${number}` : type;
    }

    accountRoleSeverity(role: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch ((role || '').toUpperCase()) {
            case 'RECEIVABLE':
                return 'info';
            case 'PAYABLE':
                return 'warn';
            default:
                return 'secondary';
        }
    }

    diagnosticSeverity(severity: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch ((severity || '').toUpperCase()) {
            case 'ERROR':
                return 'danger';
            case 'WARN':
            case 'WARNING':
                return 'warn';
            case 'INFO':
                return 'info';
            default:
                return 'secondary';
        }
    }

    exportExcel(): void {
        if (!this.selectedPartnerLedgerSummaries.length) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no partner ledger data to export.' });
            return;
        }
        this.generateExcelFile(this.selectedPartnerLedgerSummaries, 'PartnerLedgerReport');
        this.messageService.add({ severity: 'success', summary: 'Exported', detail: 'Partner ledger exported to Excel.' });
    }

    exportPartnerExcel(summary: DisplayPartnerLedgerSummary): void {
        if (!summary.partnerLedgers?.length) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no data to export for this partner.' });
            return;
        }
        this.generateExcelFile([summary], `PartnerLedger_${summary.partnerName}`);
        this.messageService.add({ severity: 'success', summary: 'Exported', detail: `Ledger for ${summary.partnerName} exported to Excel.` });
    }

    printReport(): void {
        if (!this.selectedPartnerLedgerSummaries.length) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no partner ledger data to print.' });
            return;
        }
        this.generatePrintableHtml(this.selectedPartnerLedgerSummaries, 'Partner Ledger');
    }

    printPartnerReport(summary: DisplayPartnerLedgerSummary): void {
        if (!summary.partnerLedgers?.length) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no data to print for this partner.' });
            return;
        }
        this.generatePrintableHtml([summary], `Partner Ledger: ${summary.partnerName}`);
    }

    private getCurrentFilters(): any {
        const selectedPartnerTypes = this.selectedPartnerOptions.length
            ? Array.from(new Set(this.selectedPartnerOptions.map(option => option.partnerType)))
            : this.filterPartnerTypes;

        return {
            startDate: this.toApiDate(this.filterDateRange?.[0]),
            endDate: this.toApiDate(this.filterDateRange?.[1]),
            partnerTypes: selectedPartnerTypes.length ? selectedPartnerTypes : undefined,
            partnerIds: this.selectedPartnerOptions.length
                ? Array.from(new Set(this.selectedPartnerOptions.map(option => option.partnerId)))
                : undefined,
            journals: this.filterJournals.length ? this.filterJournals : undefined,
            currencyCodes: this.filterCurrencyCodes.length ? this.filterCurrencyCodes : undefined,
            accountRole: this.filterAccountRole || undefined,
            transactionNumber: this.filterTransactionNumber || undefined,
            reference: this.filterReference || undefined,
            description: this.filterDescription || undefined,
            logisticsProjectId: this.filterLogisticsProjectId || undefined
        };
    }

    private normalizeLedgerRows(rows: PartnerLedger[]): PartnerLedger[] {
        return rows.map(row => ({
            ...row,
            transactionDate: row.transactionDate ? new Date(row.transactionDate) : undefined,
            debit: Number(row.debit ?? 0),
            credit: Number(row.credit ?? 0),
            balance: Number(row.balance ?? 0),
            openingBalance: Number(row.openingBalance ?? 0),
            closingBalance: Number(row.closingBalance ?? row.balance ?? 0)
        }));
    }

    private buildSummaries(rows: PartnerLedger[]): DisplayPartnerLedgerSummary[] {
        const grouped = new Map<string, PartnerLedger[]>();
        rows.forEach(row => {
            const key = `${row.partnerType || 'Unknown'}|${row.partnerId || 0}|${this.safeCurrencyCode(row.currencyCode || this.reportCurrencyCode)}`;
            if (!grouped.has(key)) {
                grouped.set(key, []);
            }
            grouped.get(key)?.push(row);
        });

        return Array.from(grouped.entries()).map(([key, groupRows]) => {
            const first = groupRows[0];
            const summary = new PartnerLedgerSummary() as DisplayPartnerLedgerSummary;
            summary.partnerKey = key;
            summary.partnerId = first.partnerId ?? 0;
            summary.partnerName = first.partnerName ?? 'Unknown Partner';
            summary.partnerType = first.partnerType ?? 'Unknown';
            summary.currencyCode = this.safeCurrencyCode(first.currencyCode || this.reportCurrencyCode);
            summary.partnerLedgers = groupRows;
            summary.accountRoles = this.uniqueValues(groupRows.map(row => row.accountRole));
            summary.accountLabels = this.uniqueValues(groupRows.map(row =>
                `${row.accountCode || '-'} ${row.accountName || ''}`.trim()
            ));
            this.calculateBalances(summary);
            return summary;
        }).sort((a, b) => a.partnerName.localeCompare(b.partnerName) || a.currencyCode.localeCompare(b.currencyCode));
    }

    private calculateBalances(summary: DisplayPartnerLedgerSummary): void {
        const partitionBalances = new Map<string, { opening: number; closing: number }>();
        summary.partnerLedgers.forEach(row => {
            const partitionKey = `${row.accountRole || ''}|${row.accountId || 0}|${row.currencyCode || summary.currencyCode}`;
            if (!partitionBalances.has(partitionKey)) {
                partitionBalances.set(partitionKey, {
                    opening: Number(row.openingBalance ?? 0),
                    closing: Number(row.balance ?? row.closingBalance ?? 0)
                });
            }
        });

        summary.totalDebit = summary.partnerLedgers.reduce((acc, ledger) => acc + Number(ledger.debit ?? 0), 0);
        summary.totalCredit = summary.partnerLedgers.reduce((acc, ledger) => acc + Number(ledger.credit ?? 0), 0);
        summary.openingBalance = Array.from(partitionBalances.values()).reduce((acc, value) => acc + value.opening, 0);
        summary.closingBalance = Array.from(partitionBalances.values()).reduce((acc, value) => acc + value.closing, 0);
        summary.totalBalance = summary.closingBalance;
        summary.openBalance = summary.closingBalance;
        summary.totalRowCount = summary.partnerLedgers.length;
    }

    private mergeFilterOptions(rows: PartnerLedger[]): void {
        const partnerMap = new Map(this.partnerOptions.map(option => [`${option.partnerType}|${option.partnerId}`, option]));
        rows.forEach(row => {
            if (row.partnerId && row.partnerType) {
                const key = `${row.partnerType}|${row.partnerId}`;
                partnerMap.set(key, {
                    label: `${row.partnerName || 'Unknown Partner'} (${row.partnerType})`,
                    partnerId: row.partnerId,
                    partnerType: row.partnerType
                });
            }
        });
        this.partnerOptions = Array.from(partnerMap.values()).sort((a, b) => a.label.localeCompare(b.label));

        this.partnerTypeOptions = this.mergeStringOptions(this.partnerTypeOptions, rows.map(row => row.partnerType));
        this.journalOptions = this.mergeStringOptions(this.journalOptions, rows.map(row => row.journal));
        this.currencyOptions = this.mergeStringOptions(this.currencyOptions, rows.map(row => row.currencyCode));
    }

    private mergeStringOptions(current: string[], incoming: Array<string | undefined>): string[] {
        return Array.from(new Set([
            ...current,
            ...incoming.filter((value): value is string => !!value)
        ])).sort((a, b) => a.localeCompare(b));
    }

    private uniqueValues(values: Array<string | undefined>): string[] {
        return Array.from(new Set(values.filter((value): value is string => !!value)));
    }

    private updateMetrics(summary?: LedgerReportSummary): void {
        const opening = summary?.openingBalance ?? 0;
        const debits = summary?.totalDebits ?? 0;
        const credits = summary?.totalCredits ?? 0;
        const closing = summary?.closingBalance ?? 0;
        const currencyCode = summary?.currencyCode || this.reportCurrencyCode;
        this.metrics = [
            {
                label: 'Opening Balance',
                value: this.formatMoney(opening, currencyCode),
                hint: 'Before selected period',
                tone: 'info'
            },
            {
                label: 'Debits',
                value: this.formatMoney(debits, currencyCode),
                hint: 'Filtered movement',
                tone: 'danger'
            },
            {
                label: 'Credits',
                value: this.formatMoney(credits, currencyCode),
                hint: 'Filtered movement',
                tone: 'success'
            },
            {
                label: 'Closing / Open',
                value: this.formatMoney(closing, currencyCode),
                hint: `${summary?.recordCount ?? 0} entries`,
                tone: 'info'
            }
        ];
    }

    private generateExcelFile(summaries: DisplayPartnerLedgerSummary[], fileName: string): void {
        const dataToExport: any[][] = [];
        dataToExport.push(['Partner Ledger Summary']);
        dataToExport.push(['Generated On', this.formatDate(new Date())]);
        dataToExport.push([]);
        dataToExport.push([
            'Partner', 'Type', 'Currency', 'Opening', 'Debit', 'Credit', 'Closing',
            'Open Balance', 'Entries', 'Account Roles'
        ]);

        summaries.forEach(summary => {
            dataToExport.push([
                summary.partnerName,
                summary.partnerType,
                summary.currencyCode,
                summary.openingBalance,
                summary.totalDebit,
                summary.totalCredit,
                summary.closingBalance,
                summary.openBalance,
                summary.totalRowCount,
                summary.accountRoles.join(', ')
            ]);
        });

        dataToExport.push([]);
        dataToExport.push([
            'Partner', 'Date', 'Document', 'Account', 'Journal', 'Reference', 'Description',
            'Debit', 'Credit', 'Balance', 'Currency', 'Source', 'Journal Entry ID', 'Journal Line ID'
        ]);

        summaries.forEach(summary => {
            summary.partnerLedgers.forEach(item => {
                dataToExport.push([
                    summary.partnerName,
                    this.formatDate(item.transactionDate),
                    item.transactionNumber || item.sourceDocumentNumber || '',
                    `${item.accountCode || ''} ${item.accountName || ''}`.trim(),
                    item.journal || '',
                    item.reference || '',
                    item.description || '',
                    Number(item.debit ?? 0),
                    Number(item.credit ?? 0),
                    Number(item.balance ?? 0),
                    item.currencyCode || summary.currencyCode,
                    this.sourceLabel(item),
                    item.journalEntryId || '',
                    item.journalItemId || ''
                ]);
            });
        });

        const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(dataToExport);
        const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, fileName);
    }

    private generatePrintableHtml(summaries: DisplayPartnerLedgerSummary[], title: string): void {
        const company = this.sessionStorageService.getCompany();
        const user = this.sessionStorageService.getCustomer();
        const generationDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const fileName = title.replace(/[^a-z0-9]/gi, '_') + '_' + new Date().toISOString().slice(0, 10);

        const styles = `
            <style>
                @media print { body { -webkit-print-color-adjust: exact; } }
                body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #172033; font-size: 10px; }
                .report-header { display: flex; justify-content: space-between; gap: 20px; align-items: flex-start; border-bottom: 2px solid #334155; padding-bottom: 12px; margin-bottom: 18px; }
                .logo { max-height: 48px; margin-bottom: 6px; }
                h1, h2, h3 { margin: 0; }
                .muted { color: #64748b; }
                .summary-table, .data-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
                .summary-table th, .summary-table td, .data-table th, .data-table td { border: 1px solid #d7dee8; padding: 6px; vertical-align: top; }
                .summary-table th, .data-table th { background-color: #f1f5f9; text-align: left; }
                .partner-title { margin: 18px 0 8px; }
                .text-right { text-align: right !important; }
                small { color: #64748b; display: block; margin-top: 2px; }
            </style>
        `;

        const summaryRows = summaries.map(summary => `
            <tr>
                <td>${this.escapeHtml(summary.partnerName)}</td>
                <td>${this.escapeHtml(summary.partnerType)}</td>
                <td>${this.escapeHtml(summary.currencyCode)}</td>
                <td class="text-right">${this.escapeHtml(this.formatMoney(summary.openingBalance, summary.currencyCode))}</td>
                <td class="text-right">${this.escapeHtml(this.formatMoney(summary.totalDebit, summary.currencyCode))}</td>
                <td class="text-right">${this.escapeHtml(this.formatMoney(summary.totalCredit, summary.currencyCode))}</td>
                <td class="text-right">${this.escapeHtml(this.formatMoney(summary.closingBalance, summary.currencyCode))}</td>
                <td class="text-right">${this.escapeHtml(this.formatMoney(summary.openBalance, summary.currencyCode))}</td>
                <td class="text-right">${summary.totalRowCount}</td>
            </tr>
        `).join('');

        const detailTables = summaries.map(summary => `
            <h3 class="partner-title">${this.escapeHtml(summary.partnerName)} <span class="muted">(${this.escapeHtml(summary.partnerType)} / ${this.escapeHtml(summary.currencyCode)})</span></h3>
            <table class="data-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Document</th>
                        <th>Account</th>
                        <th class="text-right">Debit</th>
                        <th class="text-right">Credit</th>
                        <th class="text-right">Balance</th>
                    </tr>
                </thead>
                <tbody>
                    ${summary.partnerLedgers.map(item => `
                        <tr>
                            <td>${this.escapeHtml(this.formatDate(item.transactionDate))}</td>
                            <td>
                                <strong>${this.escapeHtml(item.transactionNumber || item.sourceDocumentNumber || '-')}</strong>
                                <small>${this.escapeHtml(item.journal || '')}</small>
                                ${item.reference ? `<small>Ref: ${this.escapeHtml(item.reference)}</small>` : ''}
                            </td>
                            <td>
                                <strong>${this.escapeHtml(item.accountCode || '-')}</strong>
                                <small>${this.escapeHtml(item.accountName || '')}</small>
                                <small>${this.escapeHtml(item.accountRole || '')}</small>
                            </td>
                            <td class="text-right">${this.escapeHtml(this.formatMoney(item.debit, this.rowCurrency(item, summary)))}</td>
                            <td class="text-right">${this.escapeHtml(this.formatMoney(item.credit, this.rowCurrency(item, summary)))}</td>
                            <td class="text-right">${this.escapeHtml(this.formatMoney(item.balance, this.rowCurrency(item, summary)))}</td>
                        </tr>
                        ${item.description ? `
                            <tr>
                                <td></td>
                                <td colspan="5">
                                    <small>${this.escapeHtml(item.description)}</small>
                                    <small>${this.escapeHtml(this.sourceLabel(item))} | JE ${this.escapeHtml(item.journalEntryId || '-')} | Line ${this.escapeHtml(item.journalItemId || '-')}</small>
                                </td>
                            </tr>
                        ` : ''}
                    `).join('')}
                </tbody>
            </table>
        `).join('');

        const reportHtml = `
            <html>
                <head>
                    <title>${this.escapeHtml(fileName)}</title>
                    ${styles}
                </head>
                <body>
                    <div class="report-header">
                        <div>
                            ${company?.logo ? `<img src="${this.escapeHtml(company.logo)}" alt="Company Logo" class="logo">` : ''}
                            <h1>${this.escapeHtml(company?.name || 'Your Company')}</h1>
                            <div class="muted">${this.escapeHtml(title)}</div>
                        </div>
                        <div style="text-align: right;">
                            <strong>Printed By:</strong> ${this.escapeHtml(user?.name || 'N/A')}<br>
                            <strong>Date:</strong> ${this.escapeHtml(generationDate)}
                        </div>
                    </div>

                    <table class="summary-table">
                        <thead>
                            <tr>
                                <th>Partner</th>
                                <th>Type</th>
                                <th>Currency</th>
                                <th class="text-right">Opening</th>
                                <th class="text-right">Debit</th>
                                <th class="text-right">Credit</th>
                                <th class="text-right">Closing</th>
                                <th class="text-right">Open Balance</th>
                                <th class="text-right">Entries</th>
                            </tr>
                        </thead>
                        <tbody>${summaryRows}</tbody>
                    </table>

                    ${detailTables}
                </body>
            </html>
        `;

        const printWindow = window.open('', '_blank', 'height=800,width=1200');
        if (printWindow) {
            printWindow.document.write(reportHtml);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        }
        this.messageService.add({ severity: 'info', summary: 'Print', detail: 'Print dialog initiated.' });
    }

    private formatMoney(value: number | string | null | undefined, currencyCode: string = this.reportCurrencyCode): string {
        const numericValue = Number(value) || 0;
        if (!this.isIsoCurrencyCode(currencyCode)) {
            return `${numericValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyCode || ''}`.trim();
        }
        return this.currencyPipe.transform(numericValue, currencyCode, 'symbol', '1.2-2') || '';
    }

    private formatDate(value: Date | string | null | undefined): string {
        return value ? new Date(value).toLocaleDateString('en-CA') : '';
    }

    private toApiDate(value: Date | undefined): string | undefined {
        return value ? value.toISOString().split('T')[0] : undefined;
    }

    private escapeHtml(value: unknown): string {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    private safeCurrencyCode(currencyCode: string | null | undefined): string {
        return this.isIsoCurrencyCode(currencyCode) ? currencyCode! : 'SAR';
    }

    private isIsoCurrencyCode(currencyCode: string | null | undefined): boolean {
        return /^[A-Z]{3}$/.test(currencyCode || '');
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const downloadLink: HTMLAnchorElement = document.createElement('a');
        const url = URL.createObjectURL(data);
        downloadLink.href = url;
        downloadLink.download = fileName + '_' + new Date().toISOString().slice(0, 10) + '.xlsx';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    }
}
